import { alpha, createTheme, darken } from '@mui/material/styles';
import {
  AlertHexagonFilledIcon,
  AlertTriangleFilledIcon,
  CheckCircleFilledIcon,
  InfoCircleFilledIcon,
} from 'components/icons';
import extend from 'lodash/merge';
import shadows from './shadow';
import * as TIC from './tic';

const greyDark = {
  100: '#192231',
  200: '#303846',
  300: '#474e5a',
  400: '#5e646f',
  500: '#757a83',
  600: '#8c9198',
  700: '#a3a7ad',
  800: '#babdc1',
};

const greyLight = {
  100: '#F7F8FC',
  200: '#E9E9F2',
  300: '#E4E7EB',
  400: '#CBD2D9',
  500: '#9AA5B1',
  600: '#616E7C',
  700: '#52606D',
  800: '#3E4C59',
};

const buttonStyleOverrides = () => {
  const variants = ['outlined', 'tonal', 'contained'];
  const sizes = {
    Large: 9,
    Medium: 7,
    Small: 5,
    Mini: 3,
  };
  const fontSizes = {
    large: 20,
    medium: 20,
    small: 18,
    mini: 16,
  };

  const styles = variants.reduce((acc, variant) => {
    const buffer = variant === 'contained' ? 1 : 0;
    Object.entries(sizes).forEach(([size, padding]) => {
      const key = `${variant}Size${size}`;

      acc[key] = {
        paddingTop: padding + buffer,
        paddingBottom: padding + buffer,
      };
    });
    return acc;
  }, {});
  return styles;
};

export default (brand, mode = 'light', overridePallets) => {
  const isDark = mode === 'dark';
  const themePalettes = isDark ? TIC.DARK : TIC.LIGHT;

  const palettes = extend({}, themePalettes, overridePallets);
  const buttonOverrides = buttonStyleOverrides();

  const grey = isDark ? greyDark : greyLight;
  const theme = createTheme({
    shadows: shadows,
    palette: {
      mode,
      text: palettes.text,
      default: {
        main: palettes.text.main,
      },
      primary: palettes.primary,
      secondary: palettes.secondary,
      sidebar: palettes.sidebar,
      grayed: {
        main: grey[800],
        contrastText: grey[800],
      },
      todo: {
        main: isDark ? greyLight[100] : '#72889D',
        contrastText: isDark ? greyDark[200] : '#fff',
      },
      success: palettes.success,
      info: palettes.primary,
      error: palettes.error,
      warning: palettes.warning,
      pending: {
        main: '#FFD66A',
        contrastText: '#fff',
      },
      background: palettes.background,
      cta: {
        main: palettes.cta,
        contrastText: '#fff',
      },
      neutral: palettes.grey,
      inverse: {
        main: palettes.paper,
        contrastText: palettes.primary.main,
      },
      divider: palettes.divider,
      dividerVariant: palettes.dividerVariant,
      action: {
        ...palettes.action,
        selected: palettes.action.highlightBorder,
        hover: palettes.action.highlight,
      },
      talk: palettes.info,
      note: palettes.note,
      gray: palettes.neutral,
      grey: grey, // for backward compatibility. Update with neutral
      orange: palettes.orange,
      pink: palettes.note,
      input: palettes.input,
      tooltip: palettes.tooltip,
    },
    shape: {
      borderRadius: 8,
      borderRadiusLarge: 16,
      borderRadiusMedium: 6,
      borderRadiusSmall: 4,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1400,
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: 32,
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: 28,
        lineHeight: 1.2,
        fontWeight: 600,
      },
      h3: {
        fontSize: 24,
        lineHeight: 1.2,
        fontWeight: 600,
      },
      h4: {
        fontSize: 20,
        lineHeight: 1.3,
        fontWeight: 600,
      },
      h5: {
        fontSize: 18,
        lineHeight: 1.3,
        fontWeight: 400,
      },
      h6: {
        fontSize: 16,
        lineHeight: 1.3,
      },
      subtitle1: {
        fontSize: 16,
        lineHeight: '24px',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        lineHeight: '20px',
      },
      body1: {
        fontSize: 14,
        lineHeight: '20px',
      },
      body2: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '20px',
      },
      caption: {
        fontSize: 12,
        lineHeight: '16px',
      },
      small: {
        fontSize: 10,
        lineHeight: 1.5,
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          rounded: {},
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableTouchRipple: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            flexShrink: 0,
          },
        },
      },
      MuiTypography: {
        // defaultProps: {
        //   color: palettes.text.primary,
        // },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
        styleOverrides: {
          arrow: {
            color: palettes.tooltip,
          },
          tooltip: {
            backgroundColor: palettes.tooltip,
            padding: '8px 12px',
            fontWeight: 500,
            fontSize: 14,
            color: palettes.text.variant,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            color: palettes[ownerState.color]?.contrastText,
          }),
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: <AlertHexagonFilledIcon />,
            warning: <AlertTriangleFilledIcon />,
            info: <InfoCircleFilledIcon />,
            success: <CheckCircleFilledIcon />,
          },
        },
        styleOverrides: {
          root: {
            padding: '12px 16px',
          },
          message: {
            fontWeight: 500,
            padding: 0,
          },
          icon: {
            opacity: 1,
            padding: 0,
          },
          standard: ({ ownerState }) => ({
            border: 1,
            color: palettes[ownerState.severity]?.subtleContrastText,
            borderColor: palettes[ownerState.severity]?.subtleBorder,
            backgroundColor: palettes[ownerState.severity]?.subtle,
          }),
        },
      },
      MuiButton: {
        variants: [
          {
            props: {
              variant: 'link',
            },
            style: {
              padding: 0,
              display: 'inline-flex',
              textAlign: 'initial',
              color: palettes.primary.main,
              minWidth: 'unset',
              fontWeight: 'inherit',
              fontSize: 'inherit',
              lineHeight: 1.5,
              verticalAlign: 'inherit',
              '&:hover': {
                background: 'none',
                color: darken(palettes.primary.main, 0.3),
              },
              '&:focus': {
                outline: 'none',
              },
            },
          },
          {
            props: {
              variant: 'tonal',
            },
            style: ({ theme, ownerState }) => ({
              backgroundColor: theme.palette[ownerState.color]?.subtle,
              border: `1px solid ${theme.palette[ownerState.color]?.subtleBorder}`,
              color: theme.palette[ownerState.color]?.subtleContrastText,
              '&:hover': {
                backgroundColor:
                  theme.palette[ownerState.color]?.subtleHighlight,
              },
              '&.Mui-disabled': {
                color: theme.palette[ownerState.color]?.subtleContrastText,
              },
              '& svg': {
                color: theme.palette[ownerState.color]?.main,
              },
              '&.MuiButton-colorSecondary': {
                '& svg': {
                  color: palettes.primary.main,
                },
              },
            }),
          },
          {
            props: {
              variant: 'text',
              color: 'secondary',
            },
            style: ({ theme, ownerState }) => ({
              color: theme.palette[ownerState.color]?.contrastText,
              '& svg': {
                color: 'inherit',
              },
              '&.Mui-disabled': {
                color: theme.palette[ownerState.color]?.contrastText,
              },
            }),
          },
          {
            props: {
              size: 'mini',
            },
            style: {},
          },
          {
            props: {
              rounded: true,
            },
            style: {
              borderRadius: 30,
            },
          },
          {
            props: {
              focused: true,
            },
            style: {
              borderColor: palettes.action.highlightBorder,
            },
          },
        ],
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState }) => ({
            fontSize: 14,
            paddingLeft: 16,
            paddingRight: 16,
            '&.Mui-disabled': {
              color: theme.palette[ownerState.color]?.contrastText,
              opacity: 0.5,
            },
          }),
          contained: ({ ownerState }) => ({
            '&.Mui-disabled': {
              backgroundColor: theme.palette[ownerState.color]?.main,
            },
            '&:Mui-hover': {},
            '&.MuiButton-colorSecondary': {
              color: palettes.text.secondary,
              border: `1px solid ${palettes.dividerVariant}`,
              '&.MuiButton-sizeMedium': {
                paddingTop: 7,
                paddingBottom: 7,
              },
            },
          }),
          outlined: ({ ownerState }) => ({
            borderColor: theme.palette[ownerState.color]?.border,
            '&.MuiButton-colorSecondary': {
              color: theme.palette[ownerState.color]?.contrastText,
              '&.Mui-disabled': {
                color: theme.palette[ownerState.color]?.contrastText,
              },
            },
            '&.Mui-disabled': {
              color: theme.palette[ownerState.color]?.main,
            },
          }),
          text: ({ ownerState }) => ({
            '&.MuiButton-colorSecondary:hover': {
              backgroundColor: theme.palette[ownerState.color]?.highlight,
            },
            '&.Mui-disabled': {
              color: theme.palette[ownerState.color]?.main,
            },
            '& svg': {
              color: theme.palette[ownerState.color]?.main,
            },
          }),
          textError: {
            color: palettes.error.main,
            '&:hover': {
              backgroundColor: palettes.error.subtleHighlight,
            },
          },
          sizeLarge: {
            paddingTop: 10,
            paddingBottom: 10,
            '& .MuiButton-icon svg': {
              fontSize: 20,
            },
          },

          sizeMedium: {
            paddingTop: 8,
            paddingBottom: 8,
            '& .MuiButton-icon svg': {
              fontSize: 20,
            },
          },
          sizeSmall: {
            paddingTop: 6,
            paddingBottom: 6,
            '& .MuiButton-icon svg': {
              fontSize: 18,
            },
          },
          sizeMini: {
            paddingTop: 4,
            paddingBottom: 4,
            '& .MuiButton-icon svg': {
              fontSize: 16,
            },
          },
          ...buttonOverrides,
          colorError: {
            color: palettes.error.main,
          },
        },
      },
      MuiButtonGroup: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          outlined: ({ ownerState }) => ({
            color: theme.palette[ownerState.color]?.subtleContrastText,
            backgroundColor: theme.palette[ownerState.color]?.subtle,
          }),
          contained: ({ ownerState }) => ({
            '& .MuiButton-contained': {
              borderColor: theme.palette[ownerState.color]?.border,
            },
          }),

          lastButton: {
            minWidth: 0,
          },
        },
      },
      MuiToggleButton: {
        defaultProps: {
          disableRipple: true,
          // color: 'primary',
        },
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 500,
            paddingLeft: 16,
            paddingRight: 16,
            color: palettes.text.subtle,
            transition: 'background',
            border: '1px solid transparent',
            '& .MuiSvgIcon-root svg': {
              color: palettes.text.subtle,
            },
            '&.Mui-selected': {
              color: palettes.text.secondary,
              backgroundColor: palettes.background.paper5Variant,
              borderColor: palettes.dividerVariant,
              '& .MuiSvgIcon-root svg': {
                color: 'inherit',
              },
              '&:hover': {
                backgroundColor: palettes.background.paper5Variant,
              },
            },
            '&:hover': {
              color: palettes.text.secondary,
              backgroundColor: 'transparent',
            },
          },
          sizeLarge: () => ({
            paddingTop: 6,
            paddingBottom: 6,
          }),
          sizeMedium: {
            paddingTop: 4,
            paddingBottom: 4,
          },
          sizeSmall: {
            paddingTop: 2,
            paddingBottom: 2,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          // elevation: 3,
        },
        styleOverrides: {
          root: {
            // overflow: 'inherit',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&:last-child': {
              paddingBottom: 16,
            },
          },
        },
      },
      // MuiDialogActions: {
      //   styleOverrides: {
      //     root: {
      //       paddingLeft: 24,
      //       paddingRight: 24,
      //       paddingBottom: 16,
      //     },
      //   },
      // },
      MuiPopper: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              border: '1px solid',
              borderColor: palettes.dividerVariant,
              backgroundColor: palettes.background.paper5,
              backgroundImage: 'none',
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: palettes.background.paper5,
            backgroundImage: 'none',
            border: '1px solid',
            borderColor: palettes.dividerVariant,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            paddingLeft: 8,
            paddingRight: 8,
            '& .MuiListItemIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            height: 8,
            border: '1px solid',
            borderColor: palettes.divider,
            backgroundColor: palettes.background.paper2,
            borderRadius: 8,
          },
          colorPrimary: {},
          bar: {},
        },
      },
      // MuiDatePicker: {
      //   defaultProps: {
      //     slotProps: {
      //       popper: {
      //         sx: {
      //           '& .MuiPaper-root': {
      //             border: '1px solid',
      //             borderColor: palettes.divider,
      //           },
      //         },
      //       },
      //     },
      //   },
      // },
      MuiDayCalendar: {
        styleOverrides: {
          root: {},
          weekDayLabel: {
            color: palettes.text.primary,
            fontWeight: 500,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            color: palettes.text.secondary,
            '&:focus': {
              backgroundColor: palettes.action.highlight,
              '&.Mui-selected': {
                backgroundColor: palettes.action.highlight,
              },
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: palettes.action.highlight,
              color: palettes.action.highlightContrastText,
              border: 'none',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: palettes.background.paper2Variant,
            backgroundImage: 'none',
            border: `1px solid ${palettes.divider}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: palettes.background.paper3,
            paddingTop: 12,
            paddingBottom: 12,
          },
        },
      },
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          item: {
            borderRadius: 6,
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: palettes.action.highlight,
              color: palettes.action.highlightContrastText,
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            strokeWidth: 1.5,
          },
          fontSizeMini: {
            fontSize: 16,
          },
          fontSizeSmall: {
            fontSize: 18,
          },
          fontSizeMedium: {
            fontSize: 20,
          },
          fontSizeLarge: {
            fontSize: 20,
          },
        },
      },
      MuiPaper: {
        variants: [
          {
            props: { variant: 'contained' },
            style: ({ ownerState }) => ({
              backgroundColor: palettes.background.paper5,
            }),
          },
          {
            props: { variant: 'containedBordered' },
            style: ({ ownerState }) => ({
              border: '1px solid',
              backgroundColor: palettes.background.paper5,
              borderColor: palettes.divider,
              boxShadow: shadows[ownerState.elevation],
            }),
          },
          {
            props: { variant: 'containedVariant' },
            style: ({ ownerState }) => ({
              backgroundColor: palettes.background.paper5,
              border: '1px solid',
              borderColor: palettes.dividerVariant,
              boxShadow: shadows[ownerState.elevation],
            }),
          },
          {
            props: { variant: 'outlinedVariant' },
            style: {
              border: '1px solid',
              borderColor: palettes.divider,
              backgroundColor: palettes.background.paper2,
            },
          },
          {
            props: { selected: true },
            style: ({ theme }) => ({
              borderColor: theme.palette.action.highlightBorder,
              backgroundColor: theme.palette.action.highlight,
            }),
          },
          {
            props: { variant: 'elevation' },
            style: {
              backgroundImage: 'none',
            },
          },
        ],
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          rounded: {
            borderRadius: 12,
          },
        },
      },
      MuiInputBase: {
        variants: [
          {
            props: { size: 'small' },
            style: {
              '& .MuiInputBase-input': {
                height: 20,
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              '& .MuiInputBase-input': {
                height: 28,
                '-webkit-text-fill-color': 'unset',
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            padding: 0,
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          },
          input: {
            height: 24,
            padding: '5px 12px',
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: palettes.text.secondary,
            '& .MuiButton-root': {
              padding: 0,
            },
            '& .MuiButtonBase-root': {
              '&:hover': {
                backgroundColor: 'transparent !important',
              },
            },
          },
        },
      },
      // MuiTextField: {
      //   defaultProps: {
      //     size: 'small',
      //     margin: 'normal',
      //     variant: 'outlined',
      //   },
      // },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              borderColor: palettes.error.main,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: palettes.input.highlight,
            },
          },

          notchedOutline: {
            borderColor: palettes.dividerVariant,
          },
        },
      },
      // MuiAppBar: {
      //   styleOverrides: {
      //     colorInherit: {
      //       backgroundColor: palettes.navbar,
      //       color: '#fff',
      //     },
      //     colorDefault: {
      //       backgroundColor: '#fff',
      //     },
      //   },
      // },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
          },
        },
      },
      // MuiPickersDay: {
      //   styleOverrides: {
      //     root: {
      //       fontSize: 14,
      //     },
      //   },
      // },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 8,
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          size: 'small',
        },
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              width: 48,
              height: 32,
              padding: 6,
              '& .MuiSwitch-switchBase': {
                padding: 8,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            padding: 7,
          },
          thumb: {
            width: 24,
            height: 24,
            backgroundColor: palettes.background.paper5,
            boxShadow: 'none',
          },
          switchBase: {
            color: 'rgba(0,0,0,0.38)',
            padding: 7,
            '&.Mui-checked': {
              color: palettes.input.background,
              '& + .MuiSwitch-track': {
                backgroundColor: palettes.primary.main,
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
          },
          track: {
            borderRadius: 20,
            backgroundColor: palettes.dividerVariant,
            opacity: 1,
          },
          focusVisible: {},
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: palettes.input.background,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            paddingLeft: 12,
            verticalAlign: 'middle',
            color: palettes.text.secondary,
            // '&.Mui-focused:not(.Mui-error)': {
            //   color: palettes.text,
            // },
          },
          asterisk: {
            color: palettes.error.main,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          fontWeight: 500,
          root: {
            paddingLeft: 12,
            fontSize: 12,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '10px 12px',
            '&:hover': {
              color: palettes.action.highlightContrastText,
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: palettes.divider,
          },
          text: {
            fill: palettes.text.variant,
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          // size: 'small',
        },
        styleOverrides: {
          root: {
            minHeight: 40,
            '& .MuiButtonBase-root.MuiTab-root': {
              padding: '8px 16px',
              minHeight: 36,
              minWidth: 50,
              fontWeight: 500,
            },
          },
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              minHeight: 36,
              '& .MuiButtonBase-root.MuiTab-root': {
                padding: '0 8px',
                minHeight: 36,
                minWidth: 50,
                fontSize: 13,
              },
            },
          },
        ],
      },
      MuiTable: {
        variants: [
          {
            props: { variant: 'standard' },
            style: {
              '& tr.spacer': {
                display: 'none',
              },
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: palettes.divider,
            fontSize: 14,
          },
          head: {
            fontSize: 14,
            fontWeight: 500,
            color: palettes.text.primary,
          },
          sizeMedium: {},
          body: {
            '&.MuiTableCell-sizeSmall': {
              height: 48,
            },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: palettes.dividerVariant,
          },
        },
      },
      MuiBadge: {
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              '> .MuiBadge-badge': {
                height: 16,
                minWidth: 16,
                minHeight: 16,
                fontSize: 10,
                fontWeight: 600,
                padding: '0 4px',
              },
            },
          },
        ],
      },
      MuiChip: {
        defaultProps: {
          color: 'primary',
          size: 'small',
        },
        variants: [
          {
            props: { variant: 'tonal' },
            style: ({ ownerState, theme: { palette } }) => ({
              backgroundColor: palette[ownerState.color]?.subtle,
              color: palette[ownerState.color]?.main,
              border: `1px solid ${palette[ownerState.color]?.subtleBorder}`,
              '& .MuiSvgIcon-root': {
                color: palette[ownerState.color]?.subtleContrastTextMuted,
              },
            }),
          },
          {
            props: { color: 'secondary' },
            style: ({ theme: { palette } }) => ({
              color: palette.text.secondary,
              borderColor: palettes.dividerVariant,
            }),
          },
        ],
        styleOverrides: {
          root: ({ ownerState, theme: { palette } }) => ({
            backgroundColor: palette[ownerState.color]?.main,
            color: palette[ownerState.color]?.contrastText,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 20,
            '& .MuiSvgIcon-root': {
              color: palette[ownerState.color]?.contrastTextMuted,
            },
          }),
          sizeSmall: {
            height: 20,
            '& .MuiChip-avatarSmall': {
              width: 16,
              height: 16,
            },
          },
          text: ({ ownerState, theme: { palette } }) => {
            return {
              backgroundColor: 'transparent',
              color: palette[ownerState.color]?.subtleContrastText,
              '& .MuiSvgIcon-root': {
                color: palette[ownerState.color]?.subtleContrastTextMuted,
              },
            };
          },
          outlined: ({ ownerState, theme: { palette } }) => ({
            backgroundColor: palette[ownerState.color]?.subtle,
            color: palette[ownerState.color]?.subtleContrastText,
            borderColor: palette[ownerState.color]?.subtleBorder,
            '& .MuiSvgIcon-root': {
              color: palette[ownerState.color]?.subtleContrastTextMuted,
            },
          }),
          label: {
            paddingLeft: 12,
            paddingRight: 12,
          },
          icon: {
            marginLeft: 8,
            '&.MuiChip-iconSmall': {
              fontSize: 16,
            },
          },
          clickable: {
            '&:hover': {
              backgroundColor: palettes.action.highlight,
              color: palettes.action.highlightContrastText,
            },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            '&.Mui-active': {
              '& .MuiTableSortLabel-icon': {
                color: palettes.text.subtle,
              },
            },
          },
        },
      },
    },
  });

  return theme;
};
